import type { TFunction } from 'i18next';
import { MAX_CLIP_UPLOAD_BYTES, MAX_CLIP_DURATION_SEC } from '@proref/schemas';
import type { VideoProbe } from './video-probe';

export const MAX_CLIP_MB = Math.floor(MAX_CLIP_UPLOAD_BYTES / 1024 / 1024);
export const MAX_CLIP_MIN = Math.floor(MAX_CLIP_DURATION_SEC / 60);

export function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

// Turns a probe into the message shown against the file, or null when the file is fine.
// A probe that came back unknown (durationSec null) deliberately passes: the browser couldn't read
// the file, which is not the same as the file being wrong — and MediaConvert's InputClipping still
// bounds the cost either way.
export function describeFileProblem(file: File, probe: VideoProbe | null, t: TFunction): string | null {
  if (file.size > MAX_CLIP_UPLOAD_BYTES) {
    return t('admin.clips.fileProblem.tooLarge', {
      sizeMb: (file.size / 1024 / 1024).toFixed(0),
      maxMb: MAX_CLIP_MB,
    });
  }
  if (!probe) return null;
  if (probe.durationSec !== null && probe.durationSec > MAX_CLIP_DURATION_SEC) {
    return t('admin.clips.fileProblem.tooLong', {
      duration: formatDuration(probe.durationSec),
      maxMin: MAX_CLIP_MIN,
    });
  }
  return null;
}

// The clip is named after its file at upload time — the admin renames it (plus tags, options,
// commentary) in the editor once it's Ready. Strip the extension, fall back if the name is empty,
// and clamp to the schema's 200-char title cap so a pathological filename can't 400 the presign.
export function titleFromFilename(name: string, t: TFunction): string {
  const base = name.replace(/\.[^/.]+$/, '').trim();
  return (base || t('admin.clips.untitledClip')).slice(0, 200);
}
